/**
 * VAULT EXPLORER | BÓVEDA DE ACTIVOS FORENSES
 * ROLE: Secure retrieval of client-uploaded schematics and infrastructure data.
 * BRANDING: Micheals Infrastructure Advisory (Technical Strategy)
 * LOCALIZATION: English / Argentine Spanish
 */

import React, { useState, useEffect } from 'react';
import { 
  FolderOpen, FileText, ExternalLink, 
  Clock, Shield, AlertCircle, Loader2 
} from 'lucide-react';
import { secureFetch, ROUTES } from "@utils/apiClient";

const VaultExplorer = ({ sessionId, onFilesLoaded }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVaultAssets = async () => {
      if (!sessionId) return;
      setLoading(true);
      setError(null);

      try {
        const response = await secureFetch(ROUTES.LIST_VAULT(sessionId), {
          headers: {
            'Authorization': `Bearer ${sessionId}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Acceso Denegado: ${response.status}`);
        }

        const data = await response.json();
        const vaultFiles = data.files || data.assets || [];
        
        setFiles(vaultFiles);
        if (onFilesLoaded) onFilesLoaded(vaultFiles);
        
      } catch (err) {
        console.error("Vault retrieval failure:", err);
        setError("Falla en el Protocolo de Acceso."); 
      } finally {
        setLoading(false);
      }
    };

    fetchVaultAssets();
  }, [sessionId, onFilesLoaded]);

  return (
    <div className="bg-stone-900 border border-stone-800 rounded-sm overflow-hidden shadow-2xl font-sans">
      {/* --- HEADER: SECURE CHANNEL --- */}
      <div className="px-6 py-4 border-b border-stone-800 flex justify-between items-center bg-stone-900/50">
        <div className="flex items-center gap-3">
          <FolderOpen size={18} className="text-amber-600" />
          <h3 className="text-stone-100 font-serif text-xs uppercase tracking-[0.2em]">Bóveda de Activos Forenses</h3>
        </div>
        <div className="flex items-center gap-2 text-[9px] text-stone-500 font-bold uppercase tracking-tighter">
          <Shield size={12} className="text-amber-700" /> 
          Canal de Datos Encriptado
        </div>
      </div>

      {/* --- CONTENT: FILE REGISTRY --- */}
      <div className="min-h-[200px]">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="inline-block animate-spin text-amber-600 mb-4" size={20} />
            <p className="text-stone-600 text-[10px] uppercase tracking-widest">Accediendo al Archivo Seguro...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center text-amber-900/60 flex flex-col items-center gap-3">
            <AlertCircle size={24} />
            <p className="text-xs uppercase font-bold tracking-widest">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 text-[9px] text-amber-600 underline tracking-widest uppercase"
            >
              Reintentar Conexión
            </button>
          </div>
        ) : files.length === 0 ? (
          <div className="p-16 text-center">
            <p className="text-stone-500 text-xs italic font-serif text-balance">
              No se detectó documentación forense en este nodo de proyecto.
            </p>
            <p className="text-stone-700 text-[9px] uppercase tracking-tighter mt-2">
              Awaiting Client Schematic Upload
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-stone-800/50">
            {files.map((file, idx) => (
              <li key={idx} className="hover:bg-stone-800/30 transition-colors p-4 flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-stone-800 rounded text-stone-400 group-hover:text-amber-600 transition-colors">
                    <FileText size={20} />
                  </div>
                  <div>
                    <p className="text-stone-200 text-sm font-medium truncate max-w-[250px]">
                      {file.name}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-stone-500 font-mono">
                      <span className="flex items-center gap-1 uppercase tracking-tighter">
                        <Clock size={10} /> {file.uploadedAt || 'ARCHIVADO'}
                      </span>
                      <span className="bg-stone-800 px-1.5 py-0.5 rounded text-stone-400">
                        {file.size}
                      </span>
                    </div>
                  </div>
                </div>

                <a 
                  href={file.url} 
                  target="_blank" 
                  rel="noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-400 hover:text-white hover:bg-stone-700 rounded transition-all"
                >
                  Revisar <ExternalLink size={14} />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* --- FOOTER: SYSTEM STATUS --- */}
      <div className="px-6 py-3 bg-stone-950/80 border-t border-stone-800 flex justify-between items-center">
        <span className="text-[9px] text-stone-600 uppercase font-bold tracking-widest">
          Activos Totales: {files.length}
        </span>
        <span className="text-[9px] text-amber-900/50 italic font-serif">
          Revisión de Michael Obligatoria
        </span>
      </div>
    </div>
  );
};

export default VaultExplorer;